<template>
  <div class="container__wrapper">
    <Topbar>
      <div class="topbar__panel">
        <div class="topbar__heading">
          <h1>Counters</h1>
          <Breadcrumb />
        </div>
        <div class="button-group">
          <button
            class="button button--light button--pill"
            @click.prevent="openModal()"
          >
            <span class="button__icon"><i class="icon icon-e-add" /></span>
            <span class="button__label">New Record</span>
          </button>
        </div>
      </div>

      <TopbarPanel>
        <div class="navlinks__item">
          <InertiaLink
            href="/counters"
            :class="{ 'navlinks__link--active': isUrlExact(`counters`) }"
            class="navlinks__link"
          >
            All
          </InertiaLink>
        </div>
        <div class="navlinks__item">
          <InertiaLink
            href="/counters?filter[trashed]=only"
            :class="{ 'navlinks__link--active': isUrl(`counters?filter[trashed]=only`) }"
            class="navlinks__link"
          >
            Trashed
          </InertiaLink>
        </div>
      </TopbarPanel>
    </Topbar>

    <Sidebar />

    <div class="content">
      <div class="content__wrapper">
        <NoContent v-if="!counters.data.length">
          <button
            class="button u-br"
            @click.prevent="openModal()"
          >
            <span class="button__icon">
              <i class="icon icon-e-add" />
            </span>
            <span class="button__label">New Counter</span>
          </button>
        </NoContent>

        <div
          v-if="counters.data.length"
          class="counters"
        >
          <div
            v-for="counter in counters.data"
            :key="counter.id"
            class="counters__item"
          >
            <div
              class="button-group"
            >
              <button
                v-if="counter.deleted_at"
                v-tippy="{
                  arrow : true,
                  arrowType : 'sharp',
                  placement : 'top',
                  animation : 'shift-away'
                }"
                content="Restore"
                class="button button--icon"
                @click.prevent="confirmRestore(counter)"
              >
                <i class="icon icon-restore" />
              </button>

              <button
                v-tippy="{
                  arrow : true,
                  arrowType : 'sharp',
                  placement : 'top',
                  animation : 'shift-away'
                }"
                content="Edit"
                class="button button--icon"
              >
                <i class="icon icon-pen-2" />
              </button>

              <button
                v-if="!counter.deleted_at"
                v-tippy="{
                  arrow : true,
                  arrowType : 'sharp',
                  placement : 'top',
                  animation : 'shift-away'
                }"
                content="Remove"
                class="button button--icon"
                @click.prevent="confirmDelete(counter)"
              >
                <i class="icon icon-trash-can" />
              </button>
            </div>
            <div class="data-group">
              <span class="data--muted">Name:</span>
              <InertiaLink
                :href="route('counters.show', counter.id)"
              >
                {{ counter.name }}
              </InertiaLink>
            </div>
            <div class="data-group">
              <span class="data--muted">Short Name:</span>
              <span class="data data--highlight">{{ counter.short_name }}</span>
            </div>
            <div class="data-group">
              <span class="data--muted">Priority Type:</span>
              <span class="data data--highlight">{{ counter.default_type | titlecase }}</span>
            </div>
            <div class="data-group">
              <span class="data--muted">Group:</span>
              <span class="data data--highlight">{{ counter.group_name }}</span>
            </div>
            <div class="data-group">
              <span class="data--muted">Currently Serving:</span>
              <span class="data data--highlight">{{ counter.current_count || '&mdash;' }}</span>
            </div>
            <div class="data-group">
              <span class="data--muted">Last Updated:</span>
              <span class="data data--highlight">{{ counter.updated_at | moment }}</span>
            </div>
          </div>
        </div>

        <table
          v-if="counters.data.length"
          class="table counters-table"
        >
          <thead>
            <tr>
              <th class="u-w-200">Name</th>
              <th>Short Name</th>
              <th>Default Type</th>
              <th class="u-talign-l">Group</th>
              <th>Currently Serving</th>
              <th class="u-w-150">Last Updated</th>
              <th class="u-w-50" />
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="counter in counters.data"
              :key="counter.id"
            >
              <td>
                <InertiaLink
                  :href="route('counters.show', counter.id)"
                >
                  {{ counter.name }}
                </InertiaLink>
              </td>
              <td>{{ counter.short_name }}</td>
              <td>{{ counter.default_type | titlecase }}</td>
              <td class="u-talign-l">{{ counter.group_name }}</td>
              <td>{{ counter.current_count || '&mdash;' }}</td>
              <td>{{ counter.updated_at | moment }}</td>
              <td class="u-talign-r">
                <div
                  class="button-group"
                >
                  <button
                    v-if="counter.deleted_at"
                    v-tippy="{
                      arrow : true,
                      arrowType : 'sharp',
                      placement : 'top',
                      animation : 'shift-away'
                    }"
                    content="Restore"
                    class="button button--icon"
                    @click.prevent="confirmRestore(counter)"
                  >
                    <i class="icon icon-restore" />
                  </button>

                  <button
                    v-tippy="{
                      arrow : true,
                      arrowType : 'sharp',
                      placement : 'top',
                      animation : 'shift-away'
                    }"
                    content="Edit"
                    class="button button--icon"
                  >
                    <i class="icon icon-pen-2" />
                  </button>

                  <button
                    v-if="!counter.deleted_at"
                    v-tippy="{
                      arrow : true,
                      arrowType : 'sharp',
                      placement : 'top',
                      animation : 'shift-away'
                    }"
                    content="Remove"
                    class="button button--icon"
                    @click.prevent="confirmDelete(counter)"
                  >
                    <i class="icon icon-trash-can" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import Topbar from '@components/Topbar'
import Sidebar from '@components/Sidebar'
import Breadcrumb from '@components/shared/Breadcrumb'
import Dashboard from '@layouts/Dashboard'
import NoContent from '@components/shared/NoContent'
import CounterModal from '@components/settings/CounterModal'
import TopbarPanel from '@components/shared/TopbarPanel'

export default {
  metaInfo () {
    return {
      title: 'Counters',
    }
  },
  components: {
    NoContent,
    Breadcrumb,
    TopbarPanel,
    Topbar,
    Sidebar,
  },
  layout: Dashboard,
  props: {
    counters: {
      type: Object,
      required: true,
    },
  },
  methods: {
    openModal (counter = null) {
      this.$store.dispatch('modal/open', {
        modal: CounterModal,
        counter,
      })
    },
    async confirmDelete(counter) {
      const response = await this.$store.dispatch('modal/confirm', {
        title: `Are you sure you want to delete ${counter.full_name}`,
        text: 'This item will be deleted immediately. You can\'t undo this action.',
      })

      if (response) {
        this.$inertia.delete(this.route('counters.destroy', counter.id))
      }
    },
    async confirmRestore(client) {
      const response = await this.$store.dispatch('modal/confirm', {
        title: `Are you sure you want to restore ${client.full_name}`,
        text: 'This item will be restored but any invoices or contracts may not be re-activated.',
      })

      if (response) {
        this.$inertia.post(this.route('counters.restore', client.id))
      }
    },
  },
}
</script>

